import { BASEFONT_BOLD, text as constantTextStyles } from '@/constants/Styles';
import { useWindowStore } from '@/stores/useWindowStore';
import { useThrottledValue } from '@tanstack/react-pacer';
import { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const LINE_HEIGHT_MULTIPLIER = 1.15;

export const useResponsiveTextStyles = () => {
    const { width } = useWindowStore((s) => s.dimensions);
    
    const [throttleWindowWidth] = useThrottledValue(width, {
        wait: 1500, leading: true, trailing: true
    });

    const vw = useMemo(() => throttleWindowWidth / 100, [throttleWindowWidth]);

    const textStyles = useMemo(() => StyleSheet.create({
        ...constantTextStyles,
        "4xl": {
            fontSize: clamp(10 * vw, 36, 64),
            lineHeight: clamp(10 * vw, 36, 64) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "3xl": {
            fontSize: clamp(8.5 * vw, 30, 56),
            lineHeight: clamp(8.5 * vw, 30, 56) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "xxl": {
            fontSize: clamp(6.5 * vw, 24, 48),
            lineHeight: clamp(6.5 * vw, 24, 48) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "xl": {
            fontSize: clamp(5 * vw, 20, 40),
            lineHeight: clamp(5 * vw, 20, 40) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "lg": {
            fontSize: clamp(3.5 * vw, 18, 36),
            lineHeight: clamp(3.5 * vw, 18, 36) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "md": {
            fontSize: clamp(2.5 * vw, 16, 30),
            lineHeight: clamp(2.5 * vw, 16, 30) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "sm": {
            fontSize: clamp(1.5 * vw, 14, 24),
            lineHeight: clamp(1.5 * vw, 14, 24) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "xs": {
            fontSize: clamp(1 * vw, 14, 20),
            lineHeight: clamp(1 * vw, 14, 20) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "xxs": {
            fontSize: clamp(0.8 * vw, 14, 18),
            lineHeight: clamp(0.8 * vw, 14, 18) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
        "3xs": {
            fontSize: clamp(0.6 * vw, 10, 14),
            lineHeight: clamp(0.6 * vw, 10, 14) * LINE_HEIGHT_MULTIPLIER,
            fontFamily: BASEFONT_BOLD
        },
    }), [constantTextStyles, vw]);

    const resolveVariant = useCallback((variant) => (!variant ? null : textStyles[variant]), [textStyles]);

    const resolveVariants = useCallback((variant) => {

        const variants = Array.isArray(variant) ? variant : variant?.includes(',') ? variant.split(',') : [variant];

        return variants?.reduce((acc, item) => {
            const _variant = resolveVariant(item);
            return _variant ? {...acc, ..._variant} : acc;
        }, {});

    }, [textStyles]);

    return {
        resolveVariants
    }
};